import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Allocation, ProductionOrder } from '@ecinsa/shared';
import { ApiError, api } from '../lib/api';
import { useTerminal } from '../lib/store';
import { TopBar } from '../components/TopBar';
import { OrderPicker, QuantityPad, SupervisorPin, parseQty } from '../components/pickers';
import { Field, Pill } from '../components/ui';
import { qty } from '../lib/format';

type Numbering = 'manual' | 'orderNumber' | 'dateSeq' | 'itemSeq';

const NUMBERING_LABELS: Record<Numbering, string> = {
  manual: 'Lo escribo yo',
  orderNumber: 'Número de OF',
  dateSeq: 'Fecha + secuencia',
  itemSeq: 'Artículo + fecha',
};

/**
 * Cerrar orden: recibe lo fabricado y cierra la OF.
 *
 * Equivale al «Cerrar OF» del terminal clásico, incluidas las tres fórmulas de
 * numeración automática de lote. El cierre solo se manda a SAP si el recibo se
 * contabiliza primero: si el recibo falla, la OF se queda abierta y la
 * producción no se pierde.
 */
export function CloseOrder() {
  const { catalog, state, closeOrder, notify, busy } = useTerminal();
  const navigate = useNavigate();

  const [order, setOrder] = useState<ProductionOrder | null>(null);
  const [quantity, setQuantity] = useState('');
  const [scrap, setScrap] = useState('');
  const [numbering, setNumbering] = useState<Numbering>('manual');
  const [allocations, setAllocations] = useState<Allocation[]>([]);
  const [scrapAllocations, setScrapAllocations] = useState<Allocation[]>([]);
  const [askPin, setAskPin] = useState(false);

  const params = catalog?.parameters ?? {};
  const captureScrap = params['quality.captureScrap'] === 'true';
  const closesOrder = params['production.closeOrderOnFinish'] !== 'false';

  const remaining = useMemo(
    () => (order ? Math.max(0, order.plannedQuantity - order.completedQuantity) : 0),
    [order],
  );

  const managedBy = order?.parentManagedBy ?? 'none';
  const binEnabled = order?.parentBinEnabled ?? false;
  // La numeración automática es cosa del lote. Una serie identifica una pieza
  // concreta: no se puede inventar una para diez unidades.
  const autoNumbering = managedBy === 'batch';
  const detalle = managedBy !== 'none' || binEnabled;
  const cantidad = parseQty(quantity);
  const rechazo = parseQty(scrap);
  const usaFormula = autoNumbering && numbering !== 'manual';

  const asignado = allocations.reduce((s, a) => s + a.quantity, 0);
  const asignadoRechazo = scrapAllocations.reduce((s, a) => s + a.quantity, 0);
  const faltaDetalle = detalle && !usaFormula && Math.abs(asignado - cantidad) >= 1e-6;
  const faltaRechazo = managedBy !== 'none' && rechazo > 0 && Math.abs(asignadoRechazo - rechazo) >= 1e-6;

  const submit = async (supervisorPin?: string) => {
    if (!order) return;
    const amount = parseQty(quantity);
    try {
      await closeOrder({
        orderDocEntry: order.docEntry,
        quantity: amount,
        scrapQuantity: parseQty(scrap),
        autoNumbering: autoNumbering ? numbering : 'manual',
        allocations: usaFormula ? [] : allocations.filter((a) => a.quantity > 0),
        scrapAllocations: scrapAllocations.filter((a) => a.quantity > 0),
        ...(supervisorPin ? { supervisorPin } : {}),
      });
      notify({
        kind: 'ok',
        title: closesOrder ? 'Orden cerrada' : 'Producción recibida',
        body: `OF ${order.docNum} · ${qty(amount)} ud. El recibo se está contabilizando en SAP.`,
      });
      navigate('/');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'NOT_AUTHORIZED' && !supervisorPin) {
        setAskPin(true);
        return;
      }
      notify({ kind: 'error', title: 'No se pudo cerrar la orden', body: err instanceof ApiError ? err.message : String(err) });
    }
  };

  return (
    <div className="app">
      <TopBar
        title="Cerrar orden"
        subtitle={order ? `OF ${order.docNum} · ${order.itemDescription}` : 'Elige la orden de fabricación'}
        onBack={() => (order ? setOrder(null) : navigate('/'))}
      />

      <div className="content">
        {!order ? (
          <OrderPicker
            orders={catalog?.orders ?? []}
            manualEntry={params['terminal.manualEntry'] === 'true'}
            onPick={(o) => {
              setOrder(o);
              setQuantity(String(Math.max(0, o.plannedQuantity - o.completedQuantity)));
            }}
          />
        ) : (
          <div className="grid-2">
            <div className="card stack">
              <div className="eyebrow">Cantidad fabricada</div>
              <div className="row">
                <Pill>Planificado {qty(order.plannedQuantity)}</Pill>
                <Pill tone="ok">Recibido {qty(order.completedQuantity)}</Pill>
                <Pill tone={remaining > 0 ? 'warn' : 'ok'}>Pendiente {qty(remaining)}</Pill>
              </div>
              <QuantityPad value={quantity} onChange={setQuantity} unit="ud" />
              {captureScrap ? (
                <Field label="Rechazo">
                  <input
                    className="input input--qty"
                    inputMode="decimal"
                    value={scrap}
                    onChange={(e) => setScrap(e.target.value)}
                    placeholder="0"
                  />
                </Field>
              ) : null}
            </div>

            <div className="card stack">
              <div className="eyebrow">
                {managedBy === 'serial'
                  ? 'Números de serie del producto'
                  : managedBy === 'batch'
                    ? 'Lote del producto'
                    : binEnabled
                      ? 'Ubicación de entrada'
                      : 'Producto terminado'}
              </div>

              {!detalle ? (
                <p className="muted small" style={{ margin: 0 }}>
                  {order.itemCode} no se gestiona por lotes ni series, y su almacén ({order.warehouse}) no gestiona
                  ubicaciones. No hay nada que indicar.
                </p>
              ) : null}

              {autoNumbering ? (
                <>
                  <div className="chips">
                    {(Object.keys(NUMBERING_LABELS) as Numbering[]).map((mode) => (
                      <button
                        key={mode}
                        className={`chip${numbering === mode ? ' chip--selected' : ''}`}
                        onClick={() => setNumbering(mode)}
                      >
                        {NUMBERING_LABELS[mode]}
                      </button>
                    ))}
                  </div>
                  {usaFormula ? (
                    <p className="muted small" style={{ margin: 0 }}>
                      Se generará automáticamente al contabilizar
                      {numbering === 'orderNumber' ? ` (${order.docNum})` : ''}
                      {numbering === 'itemSeq' ? ` (${order.itemCode}-AAAAMMDD)` : ''}
                      {numbering === 'dateSeq' ? ' (AAAAMMDD-001)' : ''}
                      {binEnabled ? ', y entrará en la ubicación de recepción por defecto del almacén' : ''}.
                    </p>
                  ) : null}
                </>
              ) : null}

              {detalle && !usaFormula ? (
                <Asignaciones
                  managedBy={managedBy}
                  binEnabled={binEnabled}
                  warehouse={order.warehouse}
                  quantity={cantidad}
                  value={allocations}
                  onChange={setAllocations}
                />
              ) : null}

              {managedBy !== 'none' && rechazo > 0 ? (
                <>
                  <div className="eyebrow">Lo rechazado ({qty(rechazo)} ud)</div>
                  <p className="muted small" style={{ margin: 0 }}>
                    Va a otro almacén, pero es el mismo artículo: también necesita su{' '}
                    {managedBy === 'serial' ? 'número de serie' : 'lote'}.
                  </p>
                  <Asignaciones
                    managedBy={managedBy}
                    binEnabled={false}
                    warehouse={order.warehouse}
                    quantity={rechazo}
                    value={scrapAllocations}
                    onChange={setScrapAllocations}
                  />
                </>
              ) : null}

              <div className="spacer" />
              <p className="muted small" style={{ margin: 0 }}>
                {closesOrder
                  ? 'Se recibirá la producción, se consumirán los componentes de notificación y la OF pasará a Cerrado.'
                  : 'Se recibirá la producción y se consumirán los componentes de notificación. La OF quedará abierta.'}
              </p>
              <button
                className="btn btn--ok btn--lg btn--block"
                disabled={busy || cantidad <= 0 || faltaDetalle || faltaRechazo}
                onClick={() => void submit()}
              >
                {closesOrder ? 'Recibir y cerrar OF' : 'Recibir producción'}
              </button>
              {faltaDetalle || faltaRechazo ? (
                <p className="small" style={{ margin: 0, color: 'var(--warn)' }}>
                  {faltaDetalle
                    ? `Falta repartir ${qty(Math.abs(cantidad - asignado))} ud de lo fabricado.`
                    : `Falta repartir ${qty(Math.abs(rechazo - asignadoRechazo))} ud del rechazo.`}
                </p>
              ) : null}
              {state && !state.employee.supervisor ? (
                <p className="muted small" style={{ margin: 0 }}>
                  Se pedirá el PIN de un jefe de producción para autorizarlo.
                </p>
              ) : null}
            </div>
          </div>
        )}
      </div>

      {askPin ? (
        <SupervisorPin
          action={`Cerrar la OF ${order?.docNum}`}
          onClose={() => setAskPin(false)}
          onConfirm={(pin) => {
            setAskPin(false);
            void submit(pin);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Reparto de lo fabricado entre lotes, series y ubicaciones.
 *
 * No se parece al reparto de los componentes y no puede parecerse: allí se
 * elige de lo que HAY en el almacén, y aquí todavía no hay nada. Las filas las
 * crea el operario; lo único que se puede ofrecer de SAP son las ubicaciones
 * del almacén de entrada.
 */
function Asignaciones({
  managedBy,
  binEnabled,
  warehouse,
  quantity,
  value,
  onChange,
}: {
  managedBy: 'none' | 'batch' | 'serial';
  binEnabled: boolean;
  warehouse: string;
  quantity: number;
  value: Allocation[];
  onChange: (allocations: Allocation[]) => void;
}) {
  const [bins, setBins] = useState<{ absEntry: number; code: string; description: string | null }[]>([]);
  const [binsError, setBinsError] = useState<string | null>(null);
  const [prefijo, setPrefijo] = useState('');
  const [desde, setDesde] = useState('1');

  useEffect(() => {
    if (!binEnabled || !warehouse) return;
    api
      .bins(warehouse)
      .then((r) => setBins(r.bins))
      .catch((e: Error) => setBinsError(e.message));
  }, [binEnabled, warehouse]);

  // Una fila vacía de salida: el caso normal es un solo lote para todo.
  useEffect(() => {
    if (value.length === 0 && quantity > 0 && managedBy !== 'serial') {
      onChange([{ code: '', quantity, binAbsEntry: null, binCode: null }]);
    }
  }, [value.length, quantity, managedBy, onChange]);

  const editar = (index: number, cambio: Partial<Allocation>) =>
    onChange(value.map((a, i) => (i === index ? { ...a, ...cambio } : a)));

  /**
   * Rellena una serie por unidad a partir de un prefijo y un número.
   *
   * Sin esto, recibir cincuenta piezas con número de serie es teclear cincuenta
   * líneas a mano en una pantalla táctil de taller.
   */
  const generarSeries = () => {
    const inicio = Number.parseInt(desde, 10);
    if (!Number.isFinite(inicio)) return;
    const ancho = desde.trim().length;
    const filas: Allocation[] = [];
    for (let i = 0; i < Math.floor(quantity); i++) {
      filas.push({
        code: `${prefijo}${String(inicio + i).padStart(ancho, '0')}`,
        quantity: 1,
        binAbsEntry: null,
        binCode: null,
      });
    }
    onChange(filas);
  };

  const asignado = value.reduce((s, a) => s + a.quantity, 0);
  const pendiente = quantity - asignado;

  return (
    <div className="stack" style={{ gap: 12 }}>
      {managedBy === 'serial' ? (
        <div className="row row--wrap" style={{ gap: 8, alignItems: 'flex-end' }}>
          <Field label="Prefijo">
            <input className="input" style={{ width: 150 }} value={prefijo} onChange={(e) => setPrefijo(e.target.value)} placeholder="SN-" />
          </Field>
          <Field label="Desde el nº">
            <input className="input input--qty" style={{ width: 110 }} inputMode="numeric" value={desde} onChange={(e) => setDesde(e.target.value)} />
          </Field>
          <button className="btn" onClick={generarSeries} disabled={quantity <= 0}>
            Generar {Math.max(0, Math.floor(quantity))} números
          </button>
        </div>
      ) : null}

      {binsError ? (
        <p className="small" style={{ margin: 0, color: 'var(--warn)' }}>
          No se han podido leer las ubicaciones de {warehouse}: {binsError}. Puedes contabilizar sin indicarla y SAP
          usará la de recepción por defecto del almacén.
        </p>
      ) : null}

      <div className="list" style={{ maxHeight: '38vh', overflowY: 'auto' }}>
        {value.map((fila, index) => (
          <div key={index} className="list-item" style={{ cursor: 'default', gap: 8, flexWrap: 'wrap' }}>
            {managedBy !== 'none' ? (
              <input
                className="input"
                style={{ flex: '1 1 180px' }}
                value={fila.code}
                onChange={(e) => editar(index, { code: e.target.value })}
                placeholder={managedBy === 'serial' ? 'Escanea o escribe la serie' : 'Escanea o escribe el lote'}
              />
            ) : null}
            {binEnabled ? (
              <select
                className="input"
                style={{ flex: '1 1 160px' }}
                value={fila.binAbsEntry ?? ''}
                onChange={(e) => {
                  const abs = e.target.value === '' ? null : Number(e.target.value);
                  editar(index, { binAbsEntry: abs, binCode: bins.find((b) => b.absEntry === abs)?.code ?? null });
                }}
              >
                <option value="">Ubicación por defecto</option>
                {bins.map((b) => (
                  <option key={b.absEntry} value={b.absEntry}>
                    {b.code}
                  </option>
                ))}
              </select>
            ) : null}
            <input
              className="input input--qty"
              style={{ width: 110 }}
              inputMode="decimal"
              value={String(fila.quantity)}
              disabled={managedBy === 'serial'}
              onChange={(e) => editar(index, { quantity: parseQty(e.target.value) })}
            />
            <button className="btn btn--ghost" onClick={() => onChange(value.filter((_, i) => i !== index))}>
              Quitar
            </button>
          </div>
        ))}
      </div>

      <div className="row" style={{ gap: 8 }}>
        <button
          className="btn"
          onClick={() =>
            onChange([
              ...value,
              { code: '', quantity: managedBy === 'serial' ? 1 : Math.max(0, pendiente), binAbsEntry: null, binCode: null },
            ])
          }
        >
          + Añadir {managedBy === 'serial' ? 'serie' : managedBy === 'batch' ? 'lote' : 'ubicación'}
        </button>
        <div className="spacer" />
        <span className="small" style={{ color: Math.abs(pendiente) < 1e-6 ? 'var(--ok)' : 'var(--warn)' }}>
          {Math.abs(pendiente) < 1e-6 ? 'Repartido' : `Pendiente ${qty(pendiente)} ud`}
        </span>
      </div>
    </div>
  );
}
